import React, { useState, useEffect, useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import axios from 'axios';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  // OAuth conflict dialog state
  const [showOAuthConflict, setShowOAuthConflict] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  const { login, register, loginWithOAuth } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    try {
      // Decode the JWT token to get user info
      // Use decodeURIComponent + escape to properly handle UTF-8 characters in names
      const base64Payload = response.credential.split('.')[1];
      const jsonPayload = decodeURIComponent(
        atob(base64Payload)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);

      const googleUser = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        provider: 'google'
      };

      const result = await loginWithOAuth(googleUser);
      if (result.success) {
        toast.success('Logged in with Google!');
      } else {
        toast.error('Google login failed');
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google login failed');
    }
  }, [loginWithOAuth]);

  // Initialize Google Sign-In
  useEffect(() => {
    // Only initialize when the Google button should be visible
    if (showEmailForm) return;

    const initializeGoogle = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
        });

        // Render the Google Sign-In button
        const buttonElement = document.getElementById('google-signin-button');
        if (buttonElement) {
          window.google.accounts.id.renderButton(
            buttonElement,
            {
              theme: 'outline',
              size: 'large',
              width: 320
            }
          );
        }
      } else {
        // Retry after a short delay if Google hasn't loaded yet
        setTimeout(initializeGoogle, 100);
      }
    };

    initializeGoogle();
  }, [handleGoogleCredentialResponse, showEmailForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get reCAPTCHA token
      let captchaToken = null;
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha(isLogin ? 'login' : 'register');
      }

      if (isLogin) {
        const result = await login(formData.email, formData.password, captchaToken);
        if (result.success) {
          toast.success('Login successful!');
        } else {
          toast.error(result.error);
        }
      } else {
        const result = await register(formData.email, formData.password, formData.name, captchaToken);
        if (result.success) {
          toast.success('Registration successful! Please check your email to verify your account.');
          setIsLogin(true);
        } else if (result.hasOAuth) {
          // User exists with OAuth - show conflict dialog
          setConflictData({
            email: formData.email,
            password: formData.password,
            provider: result.provider,
            userId: result.userId
          });
          setShowOAuthConflict(true);
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Handle adding password to OAuth account
  const handleAddPassword = async () => {
    if (!conflictData) return;

    setLoading(true);
    try {
      // Get reCAPTCHA token
      let captchaToken = null;
      if (executeRecaptcha) {
        captchaToken = await executeRecaptcha('add_password');
      }

      const response = await axios.post('/api/auth/add-password', {
        userId: conflictData.userId,
        password: conflictData.password,
        captchaToken
      });

      if (response.data.success) {
        toast.success('Password added! You can now login with email/password.');
        setShowOAuthConflict(false);
        setConflictData(null);
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add password');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConflict = () => {
    setShowOAuthConflict(false);
    setConflictData(null);
    window.location.href = '/';
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'url("/DesktopLandingPageBG.jpg") center/cover no-repeat fixed',
      position: 'relative'
    }}>
      {/* Background overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.3)',
        zIndex: 0
      }} />
      <div style={{
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        width: '100%',
        maxWidth: '400px',
        zIndex: 1,
        position: 'relative'
      }}>
        <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
          {isLogin ? 'Login to Festivaleer' : 'Create Account'}
        </h2>

        {/* Google Sign-In Button - only show when email form is hidden */}
        {!showEmailForm && (
          <>
            <div id="google-signin-button" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '20px',
              minHeight: '44px'
            }}></div>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => setShowEmailForm(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                Log in with email and password
              </button>
            </div>
          </>
        )}

        {/* Email/password form */}
        {showEmailForm && (
          <>
            {/* Back link */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={() => setShowEmailForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                ← Back to Google Sign-In
              </button>
            </div>
            <div style={{
              textAlign: 'center',
              margin: '20px 0',
              position: 'relative',
              color: '#666'
            }}>
              <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0' }} />
              <span style={{
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '0 15px',
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)'
              }}>
                or
              </span>
            </div>

            <form onSubmit={handleSubmit}>
              {!isLogin && (
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ width: '100%', marginBottom: '20px' }}
              >
                {loading ? 'Processing...' : (isLogin ? 'Login' : 'Register')}
              </button>
            </form>

            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {isLogin ? 'Need an account? Register' : 'Already have an account? Login'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* OAuth Conflict Dialog */}
      {showOAuthConflict && conflictData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>Account Already Exists</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              An account with this email already exists and uses {conflictData.provider} login.
              Would you like to add a password to this account, or login with {conflictData.provider}?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={handleAddPassword}
                disabled={loading}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                {loading ? 'Processing...' : 'Add Password to Account'}
              </button>
              <button
                onClick={() => {
                  setShowOAuthConflict(false);
                  setConflictData(null);
                  // Trigger Google login if provider is google
                  if (conflictData.provider === 'google' && window.google) {
                    window.google.accounts.id.prompt();
                  }
                }}
                className="btn btn-secondary"
                style={{ width: '100%', background: '#6c757d', color: 'white', border: 'none', padding: '10px', borderRadius: '5px' }}
              >
                Login with {conflictData.provider.charAt(0).toUpperCase() + conflictData.provider.slice(1)}
              </button>
              <button
                onClick={handleCancelConflict}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
